import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import * as Yup from "yup";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";
import { toast } from "react-toastify";

import ClosureReportTable from "components/Common/ClosureCalculationReportTable";
import Loaders from "components/Common/Loaders";
import ClosureReportCandidatesModal from "pages/AllCommonModal/ClosureReportCandidatesModal/closure-report-candidates-modal";
import {
    AsyncClientSelect,
    AsyncUserSelect,
    StaticFilterSelect,
} from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    applyUserAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    buildUserAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    getUserAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { fetchLeadsAddedTeamOptions } from "model/LeadsAddedReportModel";
import {
    fetchClosureReportJobOptions,
    submitClosureReportFilterData,
} from "model/ClosureReportModel";

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const sumJobClosures = (jobs = []) =>
    (Array.isArray(jobs) ? jobs : []).reduce(
        (sum, job) => sum + toNumber(job?.closure ?? job?.closureCount ?? job?.count),
        0
    );

const mapJobsFromApi = (jobs = []) =>
    (Array.isArray(jobs) ? jobs : []).map((job = {}) => {
        const candidates = Array.isArray(job?.candidates) ? job.candidates : [];
        const closure = toNumber(job?.closureCount ?? job?.closure ?? 0);

        return {
            ...job,
            jobId: job?.jobId ?? "",
            job: job?.jobName ?? job?.job ?? "",
            closure,
            candidates,
            isClickable: closure > 0,
        };
    });

const mapCategoryFromApi = (category = {}) => {
    const jobs = mapJobsFromApi(category?.jobs ?? []);
    const total = toNumber(category?.categoryTotal, sumJobClosures(jobs));

    return {
        ...category,
        jobCategoryId: category?.jobCategoryId ?? "",
        jobCategory: category?.jobCategoryName ?? category?.jobCategory ?? "",
        jobs,
        total,
    };
};

const mapUserGroupFromApi = (item = {}, index = 0) => {
    const categories = (Array.isArray(item?.jobCategories) ? item.jobCategories : []).map(
        mapCategoryFromApi
    );

    const grandTotal = toNumber(
        item?.userTotal,
        categories.reduce((sum, category) => sum + toNumber(category?.total), 0)
    );

    return {
        ...item,
        id: item?.userId ?? `user-${index}`,
        userId: item?.userId ?? "",
        name: item?.userName ?? "",
        categories,
        grandTotal,
        userTotal: grandTotal,
    };
};

const closureReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object" && !Array.isArray(responseData.data)
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : [];

    return { rows };
};

const ClosureReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        jobId: "",
        teamId: "",
        userId: "",
        [getUserAsyncFilterLabelFieldKey()]: "",
    });
    const [jobOptions, setJobOptions] = useState([]);
    const [teamOptions, setTeamOptions] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterErrors, setFilterErrors] = useState({});
    const [candidatesModalOpen, setCandidatesModalOpen] = useState(false);
    const [candidatesModalContext, setCandidatesModalContext] = useState(null);

    const filteredData = useMemo(() => reportData, [reportData]);

    useEffect(() => {
        const loadTeamOptions = async () => {
            const teams = await fetchLeadsAddedTeamOptions();
            setTeamOptions(Array.isArray(teams) ? teams : []);
        };
        loadTeamOptions();
    }, []);

    useEffect(() => {
        const loadJobOptions = async () => {
            const payload = filterForm?.clientId
                ? { clientId: filterForm.clientId }
                : {};
            const jobs = await fetchClosureReportJobOptions(payload);
            setJobOptions(Array.isArray(jobs) ? jobs : []);
        };
        loadJobOptions();
    }, [filterForm?.clientId]);

    const buildFilterPayload = () => ({
        clientId: strOrNull(filterForm?.clientId),
        jobId: strOrNull(filterForm?.jobId),
        teamId: strOrNull(filterForm?.teamId),
        userId: strOrNull(filterForm?.userId),
        dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
        dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
    });

    const getClosureReportList = async () => {
        try {
            await closureReportFilterSchema.validate(filterForm, { abortEarly: false });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await submitClosureReportFilterData(buildFilterPayload());
            const { rows } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setReportData((Array.isArray(rows) ? rows : []).map(mapUserGroupFromApi));
        } catch (error) {
            toast.error(error?.message || "Unable to load closure report");
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    const handleJobChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            jobId: option?.value || "",
        }));
    };

    const handleTeamChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            teamId: option?.value || "",
        }));
    };

    const handleClientChange = (option) => {
        setFilterForm((previous) => ({
            ...applyClientAsyncFilterOptionToFormState(previous, option),
            jobId: "",
        }));
    };

    const handleClosureCountClick = (context = {}) => {
        setCandidatesModalContext(context);
        setCandidatesModalOpen(true);
    };

    const closeCandidatesModal = () => {
        setCandidatesModalOpen(false);
        setCandidatesModalContext(null);
    };

    return (
        <React.Fragment>
            <div className="page-content session-activity-report-page closure-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-2">Closure Report</h5>
                            <hr className="mt-0 mb-2" />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 align-items-end session-activity-filter-row w-100">
                                    <Col xs="12" sm="6" lg="2" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.fromDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.fromDate} />
                                    </Col>
                                    <Col xs="12" sm="6" lg="2" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label">End Date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.toDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.toDate} />
                                    </Col>
                                    <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label">Client</span>
                                        <div className="w-100 session-activity-filter-select">
                                            <AsyncClientSelect
                                                key={`closure-report-client-${filterForm?.clientId || ""}`}
                                                pageName="ReportFiltered"
                                                value={filterForm?.clientId || ""}
                                                initialOption={buildClientAsyncFilterInitialOption(
                                                    filterForm?.clientId,
                                                    filterForm[getClientAsyncFilterLabelFieldKey()]
                                                )}
                                                onChange={handleClientChange}
                                                placeholder="Select Client"
                                            />
                                        </div>
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" sm="6" lg="2" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label">Job</span>
                                        <div className="w-100 session-activity-filter-select">
                                            <StaticFilterSelect
                                                options={jobOptions}
                                                placeholder="Select Job"
                                                value={filterForm?.jobId || ""}
                                                onChange={handleJobChange}
                                            />
                                        </div>
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label">Team</span>
                                        <div className="w-100 session-activity-filter-select">
                                            <StaticFilterSelect
                                                options={teamOptions}
                                                placeholder="Select Team"
                                                value={filterForm?.teamId || ""}
                                                onChange={handleTeamChange}
                                            />
                                        </div>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>

                                <Row className="metadata-filter-toolbar__row g-2 align-items-end session-activity-filter-row w-100 mt-0">
                                    <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label">User</span>
                                        <div className="w-100 session-activity-filter-select">
                                            <AsyncUserSelect
                                                key={`closure-report-user-${filterForm?.userId || ""}`}
                                                pageName="ClosureReport"
                                                value={filterForm?.userId || ""}
                                                initialOption={buildUserAsyncFilterInitialOption(
                                                    filterForm?.userId,
                                                    filterForm[getUserAsyncFilterLabelFieldKey()]
                                                )}
                                                onChange={(option) =>
                                                    setFilterForm((previous) =>
                                                        applyUserAsyncFilterOptionToFormState(
                                                            previous,
                                                            option
                                                        )
                                                    )
                                                }
                                                placeholder="Select User"
                                            />
                                        </div>
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" sm="6" lg="2" className="session-activity-filter-col">
                                        <span className="manage-user-filter-label" aria-hidden="true">
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getClosureReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <ClosureReportTable
                                    data={filteredData}
                                    onClosureCountClick={handleClosureCountClick}
                                />
                            )}
                        </CardBody>
                    </Card>

                    <ClosureReportCandidatesModal
                        isOpen={candidatesModalOpen}
                        onClose={closeCandidatesModal}
                        userName={candidatesModalContext?.userName}
                        jobCategoryName={candidatesModalContext?.jobCategoryName}
                        jobName={candidatesModalContext?.jobName}
                        candidates={candidatesModalContext?.candidates}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

ClosureReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(ClosureReport);
